"use client";

import { useMemo } from "react";
import { Tag } from "@/components/ui/tag";

function GradientTextParser({ text }: { text: string }) {
  return useMemo(() => {
    if (!text) return null;
    const lines = text.split("\n");
    return lines.map((line, lineIndex) => {
      const parts = line.split(/(`[^`]*`)|(\*[^*]*\*)/g).filter(Boolean);
      return (
        <span key={lineIndex}>
          {lineIndex > 0 && <br />}
          {parts.map((part, index) => {
            if (part.startsWith("`") && part.endsWith("`")) {
              const inner = part.slice(1, -1);
              const isItalic = inner.startsWith("*") && inner.endsWith("*");
              const content = isItalic ? inner.slice(1, -1) : inner;
              return (
                <span
                  key={index}
                  className={`bg-[url('/images/footer_gradient-low-res.webp')] text-transparent bg-center bg-clip-text bg-cover bg-no-repeat${isItalic ? " italic" : ""}`}
                >
                  {content}
                </span>
              );
            }
            if (part.startsWith("*") && part.endsWith("*")) {
              return <em key={index}>{part.slice(1, -1)}</em>;
            }
            return part;
          })}
        </span>
      );
    });
  }, [text]);
}

type Card = {
  title?: string;
  description?: string;
  _key?: string;
};

export interface OtelSectionProps {
  tag?: string;
  eyebrow?: string;
  heading?: string;
  description?: string;
  pills?: string[];
  cards?: Card[];
  paddingTop?: string;
  paddingBottom?: string;
}

export default function OtelSection({
  tag,
  eyebrow,
  heading,
  description,
  pills,
  cards,
  paddingTop = "none",
  paddingBottom = "none",
}: OtelSectionProps) {
  const ptClass = `section-pt-${paddingTop ?? "none"}`;
  const pbClass = `section-pb-${paddingBottom ?? "none"}`;

  return (
    <section className={`w-full ${ptClass} ${pbClass}`}>
      <div className="container mx-auto px-4 xl:px-[54px]">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-center max-w-7xl mx-auto">
          {/* Left column */}
          <div>
            {tag && (
              <div className="flex mb-6">
                <Tag variant="content">{tag}</Tag>
              </div>
            )}
            {eyebrow && (
              <p className="text-xxs font-mono text-grey-600 font-bold tracking-[0.14em] uppercase mb-4 m-0">
                {eyebrow}
              </p>
            )}
            {heading && (
              <h2
                className="text-white font-normal m-0 mb-4 leading-[130%] text-[36px]"
                style={{ fontFamily: "var(--font-roobert)" }}
              >
                <GradientTextParser text={heading} />
              </h2>
            )}
            {description && (
              <p className="text-base leading-[1.7] m-0 text-grey-400">
                {description}
              </p>
            )}
            {pills && pills.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6">
                {pills.map((pill, i) => (
                  <span
                    key={i}
                    className="text-[11px] font-semibold px-2.5 py-1 rounded-sm bg-grey-850 border border-grey-800 text-grey-400 tracking-lg"
                  >
                    {pill}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Right column — stacked cards */}
          {cards && cards.length > 0 && (
            <div className="flex flex-col gap-4">
              {cards.map((card, i) => (
                <div
                  key={card._key ?? i}
                  className="rounded-lg bg-grey-900 p-5"
                  style={{ border: "1px solid #2C2C2C" }}
                >
                  {card.title && (
                    <p
                      className="text-[20px] font-bold text-white m-0 mb-1"
                      style={{ fontFamily: "var(--font-roobert)" }}
                    >
                      {card.title}
                    </p>
                  )}
                  {card.description && (
                    <p className="text-base leading-[1.6] m-0 text-grey-400">
                      {card.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
